import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { copy } from "@/copy";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import CheckpointPage from "@/pages/CheckpointPage";
import MyReflectionsPage from "@/pages/MyReflectionsPage";
import SummaryPage from "@/pages/SummaryPage";
import ProfilePage from "@/pages/ProfilePage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import AdminPage from "@/pages/AdminPage";
import ComparePage from "@/pages/ComparePage";
import AboutPage from "@/pages/AboutPage";
import OnboardingPage from "@/pages/OnboardingPage";
import RecoveryPage from "@/pages/RecoveryPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import VerificationPendingPage from "@/pages/VerificationPendingPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AppFooter from "@/components/AppFooter";

// Loading spinner (shared)
const LoadingSpinner = () => (
  <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#1F1F1F] font-medium">{copy.common.loading}</p>
    </div>
  </div>
);

// Protected Route — requires login only
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Onboarding Route — requires login, blocks access if onboarding already done
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_completed) return <Navigate to="/dashboard" replace />;
  return children;
};

// Dashboard Route — requires login + completed onboarding
const DashboardRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
};

// Admin Route — strictly requires user.is_admin === true
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Public Route — redirects logged-in users based on onboarding status
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) {
    return <Navigate to={user.onboarding_completed ? '/dashboard' : '/onboarding'} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* About Page - Accessible to all */}
      <Route path="/about" element={<AboutPage />} />

      {/* Onboarding — new users only, blocked for returning users */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <DashboardRoute>
            <DashboardPage />
          </DashboardRoute>
        }
      />
      <Route
        path="/checkpoint/:checkpointId"
        element={
          <ProtectedRoute>
            <CheckpointPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reflections"
        element={
          <ProtectedRoute>
            <MyReflectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary"
        element={
          <ProtectedRoute>
            <SummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/settings"
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/compare"
        element={
          <AdminRoute>
            <ComparePage />
          </AdminRoute>
        }
      />

      {/* Public recovery routes */}
      <Route path="/recover" element={<RecoveryPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verification-pending" element={<VerificationPendingPage />} />

      {/* Legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#F5F5F5] flex flex-col">
          <div className="flex-1">
            <AppRoutes />
          </div>
          <AppFooter />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
